import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  HandCoins,
  Users,
  Plus,
  Search,
  Euro,
  Mail,
  Phone,
  MoreHorizontal,
  Eye,
  Loader2,
  TrendingUp,
  Percent
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// AFFILIATES PAGE V1 - Tableau compact
// =============================================

const AffiliatesPageV1 = () => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [stats, setStats] = useState({
    totalAffiliates: 0,
    totalCommissions: 0,
    pendingPayments: 0
  });

  const [affiliates, setAffiliates] = useState([]);

  const [newAffiliate, setNewAffiliate] = useState({
    name: '',
    email: '',
    phone: '',
    commission_rate: 10
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [affiliatesRes] = await Promise.all([
        api.get('/affiliates/')
      ]);
      
      const affiliatesData = affiliatesRes.data || [];
      setAffiliates(affiliatesData);
      
      // Calculate stats
      const totalCommissions = affiliatesData.reduce((sum, a) => sum + (a.total_earned || 0), 0);
      const pendingPayments = affiliatesData.reduce((sum, a) => sum + (a.pending_commission || 0), 0);
      
      setStats({
        totalAffiliates: affiliatesData.length,
        totalCommissions,
        pendingPayments
      });
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliate = async () => {
    if (!newAffiliate.name || !newAffiliate.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setCreating(true);
      await api.post('/affiliates/', newAffiliate);
      toast.success('Apporteur créé avec succès');
      setShowNewModal(false);
      setNewAffiliate({ name: '', email: '', phone: '', commission_rate: 10 });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const filteredAffiliates = affiliates.filter(a => 
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Apporteurs d'affaires</h1>
            <p className="text-sm text-slate-500">Gérez vos partenaires et commissions</p>
          </div>
          <Button onClick={() => setShowNewModal(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Nouvel apporteur
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Apporteurs</p>
                <p className="text-lg font-bold text-slate-900">{stats.totalAffiliates}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Commissions versées</p>
                <p className="text-lg font-bold text-slate-900">{stats.totalCommissions.toLocaleString('fr-FR')}€</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">À verser</p>
                <p className="text-lg font-bold text-slate-900">{stats.pendingPayments.toLocaleString('fr-FR')}€</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredAffiliates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <HandCoins className="w-10 h-10 mb-2" />
                <p className="text-sm">Aucun apporteur trouvé</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Nom</TableHead>
                    <TableHead className="font-medium">Contact</TableHead>
                    <TableHead className="font-medium text-center">Taux</TableHead>
                    <TableHead className="font-medium text-center">Filleuls</TableHead>
                    <TableHead className="font-medium text-right">Commissions</TableHead>
                    <TableHead className="font-medium text-right">À verser</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAffiliates.map((affiliate) => (
                    <TableRow key={affiliate.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-medium text-sm">
                            {affiliate.name?.charAt(0) || 'A'}
                          </div>
                          <span className="font-medium text-slate-900">{affiliate.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            {affiliate.email}
                          </div>
                          {affiliate.phone && (
                            <div className="flex items-center gap-1 text-xs text-slate-400">
                              <Phone className="w-3 h-3" />
                              {affiliate.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono">
                          <Percent className="w-3 h-3 mr-1" />
                          {affiliate.commission_rate || 10}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{affiliate.referral_count || 0}</span>
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">
                        {(affiliate.total_earned || 0).toLocaleString('fr-FR')}€
                      </TableCell>
                      <TableCell className="text-right">
                        {(affiliate.pending_commission || 0) > 0 ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            {affiliate.pending_commission.toLocaleString('fr-FR')}€
                          </Badge>
                        ) : (
                          <span className="text-slate-400">0€</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Euro className="w-4 h-4 mr-2" />
                              Verser commission
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* New Affiliate Modal */}
        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvel apporteur d'affaires</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  placeholder="Nom complet"
                  value={newAffiliate.name}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="email@exemple.fr"
                  value={newAffiliate.email}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  placeholder="06 00 00 00 00"
                  value={newAffiliate.phone}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Taux de commission (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={newAffiliate.commission_rate}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, commission_rate: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewModal(false)}>Annuler</Button>
              <Button onClick={handleCreateAffiliate} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AffiliatesPageV1;
